import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdAutoAwesome, MdBolt, MdCloud, MdPlayArrow, MdTune } from 'react-icons/md';
import { toast } from 'react-toastify';
import client from '../api/client';
import { messaggioErrore, erroriPerCampo } from '../utils/apiError';
import {
  Button, ConfirmDialog, DialogBody, DialogFooter, EmptyState, Field, Modal, SkeletonList, Switch, classeCampo,
} from './ui';
import TabellaDati from './TabellaDati';
import SchedaServizio, { Parte, quando } from './SchedaServizio';

// Nomi leggibili per le impostazioni più comuni; le altre si mostrano come sono.
const NOMI_IMPOSTAZIONI = { lega: 'Lega (numero)', squadra: 'La mia squadra' };

// Una scheda per servizio, con le sue impostazioni e «Cosa puoi fare» (29/09/2026:
// «nella mia testa sono la stessa cosa»; 30/09/2026: pagina Servizi). Un servizio
// senza API è una scheda da solo. Ogni cosa che può fare è una riga: chi la usa,
// se ha un errore e quando; toccandola si aprono prova, dati, opzioni AI ed
// eliminazione, invece di tre pillole e un cestino sempre in vista.
function SchedaApi({ api, operazioni, usoDi, onApri, onElimina, onTabella, onAggiungi, onImpostazioni, onAi, onComando }) {
  const [impostazioni, setImpostazioni] = useState(Object.entries(api?.parametri || {}));
  const [salvando, setSalvando] = useState(false);
  const [espansa, setEspansa] = useState(null);
  const cambiate = JSON.stringify(Object.fromEntries(impostazioni)) !== JSON.stringify(api?.parametri || {});
  const salva = async () => {
    setSalvando(true);
    try { await onImpostazioni(Object.fromEntries(impostazioni.filter(([nome]) => nome.trim()))); }
    finally { setSalvando(false); }
  };
  const nomeDi = servizio => (api ? servizio.name.replace(`${api.name} · `, '') : servizio.name);
  const uso = [...new Set(operazioni.flatMap(o => usoDi(o.id)))];
  const conErrore = operazioni.filter(o => o.last_error).length;
  const quante = operazioni.length;
  return <SchedaServizio nome={api ? api.name : operazioni[0].name} tuo uso={uso}
    frase={api ? `${quante} ${quante === 1 ? 'cosa che può fare' : 'cose che può fare'}` : 'Un servizio collegato da te'}
    stato={conErrore ? { testo: `⚠ ${conErrore} ${conErrore === 1 ? 'errore' : 'errori'}`, tono: 'errore' } : null}>
    {api && <details className="text-sm" open={impostazioni.some(([, valore]) => !valore) || undefined}>
      <summary className="cursor-pointer">Impostazioni{impostazioni.length ? `: ${impostazioni.filter(([, v]) => v).map(([n, v]) => `${NOMI_IMPOSTAZIONI[n] || n} ${v}`).join(', ')}` : ''}</summary>
      <p className="text-xs text-muted my-2">Valgono per tutte le operazioni e le automazioni di questa API. Negli indirizzi si scrivono fra graffe, per esempio {'{{lega}}'}.</p>
      <div className="space-y-2">
        {impostazioni.map(([nome, valore], i) => <div key={i} className="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_auto] gap-2 items-center">
          <input className={classeCampo} aria-label="Nome dell’impostazione" value={nome}
            onChange={e => setImpostazioni(impostazioni.map((c, j) => (j === i ? [e.target.value, c[1]] : c)))} />
          <input className={classeCampo} aria-label={NOMI_IMPOSTAZIONI[nome] || nome} value={valore}
            onChange={e => setImpostazioni(impostazioni.map((c, j) => (j === i ? [c[0], e.target.value] : c)))} />
          <button type="button" aria-label={`Togli ${nome}`} className="text-muted" onClick={() => setImpostazioni(impostazioni.filter((_, j) => j !== i))}>✕</button>
        </div>)}
        <div className="flex gap-2">
          <button type="button" className="text-sm underline" onClick={() => setImpostazioni([...impostazioni, ['', '']])}>+ Impostazione</button>
          {cambiate && <Button variant="primary" loading={salvando} onClick={salva}>Salva impostazioni</Button>}
        </div>
      </div>
    </details>}
    <Parte titolo="Cosa puoi fare">
      <ul className="divide-y divide-line rounded-lg border border-line">
        {operazioni.map(servizio => {
          const usata = usoDi(servizio.id);
          const aperta = espansa === servizio.id;
          const descritti = servizio.schema?.colonne?.length || servizio.schema?.generali?.length;
          return <li key={servizio.id} className="px-3">
            <button type="button" aria-expanded={aperta} className="block min-h-[44px] w-full py-2 text-left"
              onClick={() => setEspansa(aperta ? null : servizio.id)}>
              <span className="block break-words text-sm font-medium text-ink">{nomeDi(servizio)}</span>
              <span className="block text-xs text-muted">
                {usata.length ? `Usata da ${usata.join(', ')}` : 'Non usata'}
                {servizio.enabled ? '' : ' · spenta'}{descritti ? '' : ' · dati da descrivere'}
              </span>
              {servizio.last_error && <span className="block break-words text-xs text-danger">
                Errore{servizio.last_error_at ? ` del ${quando(servizio.last_error_at)}` : ''}: {servizio.last_error}</span>}
            </button>
            {aperta && <div className="space-y-3 pb-3">
              <div className="flex flex-wrap gap-2">
                <Button icon={MdPlayArrow} onClick={() => onApri(servizio)}>Prova e modifica</Button>
                <Button onClick={() => onTabella(servizio)}>Dati che restituisce</Button>
                <Button onClick={() => onComando(servizio)}>+ Crea un comando</Button>
              </div>
              {/* L'aiuto dell'AI in vista (29/09/2026), detto a parole (30/09/2026). */}
              <Switch label="Capisce frasi libere" checked={!!servizio.aiuto_ai} onChange={() => onAi(servizio, 'aiuto_ai')}
                hint="L’AI legge la richiesta («il volo di Claudia domani») e compila i dati da cercare." />
              <Switch label="Risponde con parole sue" checked={!!servizio.use_ai} onChange={() => onAi(servizio, 'use_ai')}
                hint="L’AI scrive la risposta a partire dai dati, invece della frase fissa." />
              <button type="button" className="min-h-[44px] text-sm text-danger" onClick={() => onElimina(servizio)}>Elimina…</button>
            </div>}
          </li>;
        })}
      </ul>
      {api && <button type="button" className="text-sm underline" onClick={() => onAggiungi(api)}>+ Aggiungi un’altra cosa che può fare</button>}
    </Parte>
  </SchedaServizio>;
}

const vuoto = () => ({
  name: '', enabled: true, method: 'GET', url_template: '', headers: {},
  body_template: '', reply_template: '', use_ai: false, timeout_seconds: 10, secret: '',
  allow_local: false,
});

// Le intestazioni si scrivono come testo, una per riga: un editor di coppie
// nome/valore per due righe sarebbe più lavoro che aiuto.
const intestazioniInTesto = oggetto => Object.entries(oggetto || {})
  .map(([nome, valore]) => `${nome}: ${valore}`).join('\n');

export const intestazioniDaTesto = (testo) => {
  const risultato = {};
  (testo || '').split('\n').map(r => r.trim()).filter(Boolean).forEach((riga) => {
    const taglio = riga.indexOf(':');
    if (taglio > 0) risultato[riga.slice(0, taglio).trim()] = riga.slice(taglio + 1).trim();
  });
  return risultato;
};

// I dati che l'operazione accetta: i nomi semplici fra graffe negli indirizzi,
// tolte la chiave e le impostazioni dell'API (come ingressi_di sul server).
const NOME_DATO = /\{\{\s*([a-z][a-z0-9_]*)\s*(?:\|[^}]*)?\}\}/g;
export const datiDellOperazione = (bozza, intestazioni, impostazioni = {}) => {
  const nomi = [];
  [bozza.url_template, intestazioni, bozza.body_template].forEach(testo => {
    for (const trovato of String(testo || '').matchAll(NOME_DATO)) {
      const nome = trovato[1];
      if (!['chiave', 'per', 'fine'].includes(nome) && !(nome in impostazioni) && !nomi.includes(nome)) nomi.push(nome);
    }
  });
  return nomi;
};

const usaChiave = (bozza, intestazioni) => [bozza.url_template, intestazioni, bozza.body_template]
  .some(testo => (testo || '').includes('{{chiave}}'));

// Configurare un servizio voleva dire scrivere indirizzo, metodo, intestazioni e
// percorsi dei campi a memoria (29/09/2026: «dettagli tecnici solo nelle
// impostazioni avanzate»). Ora: si sceglie un servizio pronto o lo si descrive
// all'AI, si prova, si toccano i dati da mettere nella frase, si salva.
export default function ExternalServices({ usoDi = () => [], daCercare = null, children = null }) {
  const navigate = useNavigate();
  // Eliminare chiede conferma e dice chi la usa: prima il cestino cancellava
  // subito, anche un'operazione usata da un comando (30/09/2026).
  const [daEliminare, setDaEliminare] = useState(null);
  const [servizi, setServizi] = useState([]);
  const [apis, setApis] = useState([]);
  const [tabella, setTabella] = useState(null);
  const [modelli, setModelli] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [scelta, setScelta] = useState(false);
  const [descrizione, setDescrizione] = useState('');
  const [aiInCorso, setAiInCorso] = useState(false);
  const [bozza, setBozza] = useState(null);
  const [guida, setGuida] = useState(null);
  const [avanzate, setAvanzate] = useState(false);
  const [intestazioni, setIntestazioni] = useState('');
  const [errori, setErrori] = useState({});
  const [salvataggio, setSalvataggio] = useState(false);
  const [prova, setProva] = useState(null);
  const [provaInCorso, setProvaInCorso] = useState(false);
  const [testoProva, setTestoProva] = useState('');
  // Con molti dati (un volo ne ha una quarantina) serve cercarli per nome.
  const [cercaCampo, setCercaCampo] = useState('');

  const ricarica = () => Promise.all([client.get('/v1/external-services/'), client.get('/v1/external-apis/')])
    .then(([s, a]) => { setServizi(s.data); setApis(a.data); })
    .catch(e => toast.error(messaggioErrore(e)))
    .finally(() => setCaricamento(false));
  const cambiaAi = async (servizio, campo) => {
    try {
      await client.patch(`/v1/external-services/${servizio.id}/`, { [campo]: !servizio[campo] });
      ricarica();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };
  const salvaImpostazioni = api => async parametri => {
    try {
      await client.patch(`/v1/external-apis/${api.id}/`, { parametri });
      toast.success('Impostazioni salvate');
      ricarica();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };
  const gruppi = [
    ...apis.map(api => ({ api, operazioni: servizi.filter(s => s.api === api.id) })).filter(g => g.operazioni.length),
    ...servizi.filter(s => !s.api || !apis.some(a => a.id === s.api)).map(s => ({ api: null, operazioni: [s] })),
  ];

  // Da «Cosa vorresti ottenere?»: la finestra per collegarne uno, con la
  // descrizione già pronta da far leggere all'AI.
  useEffect(() => {
    if (!daCercare) return;
    setDescrizione(daCercare.testo);
    setScelta(true);
  }, [daCercare]);

  useEffect(() => {
    ricarica();
    client.get('/v1/external-services/modelli/').then(r => setModelli(r.data)).catch(() => {});
  }, []);

  const apri = (servizio, { suggerimenti = null, esperto = false, esempio = '', api = null } = {}) => {
    setScelta(false);
    setBozza(servizio ? { ...vuoto(), ...servizio, secret: '' } : { ...vuoto(), ...(api ? { api: api.id, name: `${api.name} · ` } : {}) });
    setIntestazioni(intestazioniInTesto(servizio?.headers));
    setGuida(suggerimenti);
    setAvanzate(esperto);
    setErrori({});
    setProva(null);
    setTestoProva(esempio);
  };

  const daModello = modello => apri(modello.servizio, {
    esempio: modello.esempio, suggerimenti: { parola: modello.parola, testo: modello.descrizione },
  });

  const chiediAllAi = async () => {
    setAiInCorso(true);
    try {
      const { data } = await client.post('/v1/external-services/bozza-ai/', { descrizione }, { timeout: 60000 });
      apri(data.servizio, {
        esempio: data.esempio,
        esperto: !data.servizio.url_template,
        suggerimenti: {
          parola: data.parola, testo: data.note, avviso: data.avviso,
          chiave: data.serve_chiave ? (data.dove_prendere_la_chiave || 'Questo servizio vuole una chiave.') : '',
          daAi: true,
        },
      });
    } catch (errore) {
      toast.error(messaggioErrore(errore));
    } finally {
      setAiInCorso(false);
    }
  };

  const salva = async () => {
    setSalvataggio(true);
    setErrori({});
    const corpo = { ...bozza, headers: intestazioniDaTesto(intestazioni) };
    if (!corpo.secret) delete corpo.secret;
    try {
      const risposta = bozza.id
        ? await client.patch(`/v1/external-services/${bozza.id}/`, corpo)
        : await client.post('/v1/external-services/', corpo);
      toast.success(bozza.id ? 'Servizio aggiornato' : 'Servizio salvato');
      setBozza({ ...risposta.data, secret: '' });
      ricarica();
    } catch (errore) {
      const perCampo = erroriPerCampo(errore);
      setErrori(perCampo);
      if (perCampo.url_template || perCampo.headers || perCampo.timeout_seconds) setAvanzate(true);
      toast.error(messaggioErrore(errore));
    } finally {
      setSalvataggio(false);
    }
  };

  const elimina = async (servizio) => {
    setDaEliminare(null);
    try {
      await client.delete(`/v1/external-services/${servizio.id}/`);
      toast.info('Servizio eliminato');
      ricarica();
    } catch (errore) {
      toast.error(messaggioErrore(errore));
    }
  };

  // La prova funziona anche prima di salvare: è il modo di scoprire se
  // l'indirizzo risponde e quali dati arrivano.
  const esegui = async () => {
    setProva(null);
    setProvaInCorso(true);
    try {
      const { data } = await client.post('/v1/external-services/prova-bozza/',
        { ...bozza, headers: intestazioniDaTesto(intestazioni), text: testoProva }, { timeout: 40000 });
      setProva(data);
    } catch (errore) {
      toast.error(messaggioErrore(errore));
    } finally {
      setProvaInCorso(false);
    }
  };

  const aggiungiCampo = percorso => setBozza(b => ({
    ...b, reply_template: `${(b.reply_template || '').replace(/\s*$/, '')}${b.reply_template ? ' ' : ''}{{${percorso}}}`,
  }));

  if (caricamento) return <SkeletonList rows={3} />;

  const chiaveVisibile = bozza && (usaChiave(bozza, intestazioni) || bozza.has_secret || guida?.chiave);
  return (
    <section className="space-y-3">
      {/* Il pulsante sta sopra l'elenco intero: in mezzo, fra i servizi pronti
          e quelli collegati, li faceva sembrare due categorie (30/09/2026). */}
      <div className="flex justify-end">
        <Button variant="primary" icon={MdAdd} onClick={() => setScelta(true)}>Collega un servizio</Button>
      </div>
      {children}
      {servizi.length === 0 ? (!children &&
        <EmptyState icon={MdCloud} title="Nessuna API collegata"
          hint="Collega per esempio i cambi o Wikipedia, o un’API tua, poi usala con un’automazione." />
      ) : gruppi.map(({ api, operazioni }) => <SchedaApi key={api ? `api-${api.id}` : `s-${operazioni[0].id}`}
        api={api} operazioni={operazioni} usoDi={usoDi} onApri={apri} onElimina={setDaEliminare} onTabella={setTabella} onAi={cambiaAi}
        onComando={servizio => navigate('/automations', { state: { servizio: servizio.id } })}
        onAggiungi={a => apri(null, { api: a, esperto: true })} onImpostazioni={api ? salvaImpostazioni(api) : null} />)}
      <ConfirmDialog open={!!daEliminare} onClose={() => setDaEliminare(null)} onConfirm={() => elimina(daEliminare)} danger
        title={`Eliminare «${daEliminare?.name || ''}»?`} confirmLabel="Elimina"
        message={daEliminare && usoDi(daEliminare.id).length
          ? `La usano ${usoDi(daEliminare.id).join(', ')}: quelle automazioni smetterebbero di funzionare.`
          : 'Nessuna automazione la usa.'} />
      {tabella && <TabellaDati servizio={tabella} onChiudi={() => setTabella(null)}
        onSalvata={() => { setTabella(null); ricarica(); }} />}

      {scelta && (
        <Modal open onClose={() => setScelta(false)} label="Aggiungi un servizio">
          <DialogBody>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-ink">Pronti da usare</p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {modelli.map(modello => (
                    <button key={modello.id} type="button" onClick={() => daModello(modello)}
                      className="rounded-lg border border-line p-3 text-left hover:bg-canvas">
                      <span className="flex items-center gap-2 font-medium text-ink">
                        <MdBolt aria-hidden className="text-brand-600" /> {modello.titolo}
                      </span>
                      <span className="mt-1 block text-xs text-muted">{modello.descrizione}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-line p-3">
                <p className="flex items-center gap-2 text-sm font-medium text-ink">
                  <MdAutoAwesome aria-hidden className="text-brand-600" /> Descrivilo all’AI
                </p>
                <p className="mb-2 text-xs text-muted">
                  Per esempio «gli orari dei treni da Roma» o «la classifica della Serie A». L’AI propone come
                  chiamarlo; poi lo provi prima di salvarlo.
                </p>
                <textarea className={classeCampo} rows={2} aria-label="Descrizione del servizio"
                  value={descrizione} onChange={e => setDescrizione(e.target.value)} />
                <div className="mt-2 flex justify-end">
                  <Button icon={MdAutoAwesome} loading={aiInCorso}
                    disabled={descrizione.trim().length < 5} onClick={chiediAllAi}>Proponi</Button>
                </div>
              </div>
              <button type="button" onClick={() => apri(null, { esperto: true })}
                className="flex items-center gap-2 text-sm text-muted hover:text-ink">
                <MdTune aria-hidden /> Configuro io indirizzo e dettagli tecnici
              </button>
            </div>
          </DialogBody>
        </Modal>
      )}

      {bozza && (
        <Modal open size="xl" ridimensionabile onClose={() => setBozza(null)} label={bozza.id ? bozza.name : 'Nuovo servizio'}>
          <DialogBody>
            <div className="space-y-3">
              {guida?.avviso && <p role="alert" className="rounded bg-warning-bg p-2 text-sm text-warning">{guida.avviso}</p>}
              {guida?.testo && <p className="rounded bg-brand-50 p-2 text-sm text-ink">
                {guida.daAi && 'Proposta dell’AI: provala prima di salvarla. '}{guida.testo}
              </p>}

              <Field label="Nome" error={errori.name}>
                {props => <input {...props}
                  value={bozza.name} onChange={e => setBozza({ ...bozza, name: e.target.value })} />}
              </Field>

              {chiaveVisibile && (
                <Field label="Chiave del servizio" error={errori.secret}
                  hint={bozza.has_secret
                    ? 'Una chiave è già salvata. Lascia vuoto per tenerla.'
                    : guida?.chiave || 'Viene cifrata e non verrà mai mostrata di nuovo.'}>
                  {props => <input {...props} type="password" autoComplete="new-password"
                    value={bozza.secret || ''}
                    onChange={e => setBozza({ ...bozza, secret: e.target.value })} />}
                </Field>
              )}

              <div className="rounded-lg border border-line p-3">
                <p className="text-sm font-medium text-ink">1. Prova il servizio</p>
                <p className="mb-2 text-xs text-muted">
                  Scrivi quello che metteresti dopo la parola di comando. La prova chiama davvero il servizio e
                  mostra i dati che rende; non manda niente su WhatsApp.
                </p>
                <div className="flex gap-2">
                  <input className={classeCampo} placeholder="es. Roma" value={testoProva}
                    aria-label="Testo di prova" onChange={e => setTestoProva(e.target.value)} />
                  <Button icon={MdPlayArrow} loading={provaInCorso}
                    disabled={!bozza.url_template} onClick={esegui}>Prova il servizio</Button>
                </div>
                {/* «FR 3946» restituiva zero righe: l'API vuole «FR3946» (29/09/2026). */}
                <label className="mt-2 flex items-center gap-2 text-xs text-muted">Come passare il testo
                  <select className={`${classeCampo} w-auto`} value={bozza.formato_testo || ''}
                    onChange={e => setBozza({ ...bozza, formato_testo: e.target.value })}>
                    <option value="">Così com’è</option>
                    <option value="senza_spazi">Senza spazi</option>
                    <option value="maiuscolo_senza_spazi">Maiuscolo, senza spazi (codici volo, targhe…)</option>
                    <option value="maiuscolo">Maiuscolo</option>
                  </select>
                </label>
                <label className="mt-2 flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-1" checked={!!bozza.aiuto_ai}
                    onChange={e => setBozza({ ...bozza, aiuto_ai: e.target.checked })} />
                  <span>Fatti aiutare dall’AI a capire la richiesta
                    <span className="block text-xs text-muted">L’AI legge quello che scrivi («Ryanair da Fiumicino a Palermo») e
                      compila i dati qui sotto nel formato giusto. Pochi decimi di centesimo a richiesta.</span></span>
                </label>
                {datiDellOperazione(bozza, intestazioni, apis.find(a => a.id === bozza.api)?.parametri).length > 0 && <fieldset className="mt-3 space-y-2">
                  <legend className="text-sm font-medium text-ink">Dati che l’operazione accetta</legend>
                  <p className="text-xs text-muted">Sono i nomi fra graffe dell’indirizzo. Una descrizione con un esempio aiuta l’AI a
                    compilarli; il predefinito vale quando la richiesta non lo dice. I dati vuoti spariscono dall’indirizzo.</p>
                  {datiDellOperazione(bozza, intestazioni, apis.find(a => a.id === bozza.api)?.parametri).map(nome => {
                    const dato = (bozza.ingressi || []).find(i => i.id === nome) || { id: nome };
                    const cambia = patch => setBozza({ ...bozza, ingressi: [...(bozza.ingressi || []).filter(i => i.id !== nome), { ...dato, ...patch }] });
                    return <div key={nome} className="grid gap-2 sm:grid-cols-[7rem_1fr_7rem_7rem] items-center">
                      <code className="text-xs">{`{{${nome}}}`}</code>
                      <input className={classeCampo} aria-label={`Descrizione di ${nome}`} placeholder="Descrizione, es. codice IATA dell’aeroporto"
                        value={dato.descrizione || ''} onChange={e => cambia({ descrizione: e.target.value })} />
                      <input className={classeCampo} aria-label={`Esempio di ${nome}`} placeholder="Esempio"
                        value={dato.esempio || ''} onChange={e => cambia({ esempio: e.target.value })} />
                      <input className={classeCampo} aria-label={`Predefinito di ${nome}`} placeholder="Predefinito"
                        value={dato.predefinito || ''} onChange={e => cambia({ predefinito: e.target.value })} />
                    </div>;
                  })}
                </fieldset>}
                {prova && (
                  <div className="mt-3 space-y-2">
                    {/* La prova non passa dall'AI: con la riscrittura accesa, in
                        chat arriva un'altra frase, fatta da questi dati. */}
                    <p className="text-xs text-muted">{bozza.use_ai ? 'Risposta dell’AI, come arriverà in chat:' : 'Frase pronta per la chat:'}</p>
                    {Object.keys(prova.capito || {}).length > 0 && <p className="text-xs text-brand-700">
                      L’AI ha capito: {Object.entries(prova.capito).map(([nome, valore]) => `${nome} ${valore}`).join(' · ')}</p>}
                    <p className="max-h-[40vh] overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere] rounded bg-surface-2 px-3 py-2 text-sm text-ink">{prova.text}</p>
                    {Object.keys(prova.campi || {}).length > 0 && <>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-muted">Tocca un dato per aggiungerlo alla frase qui sotto:</p>
                        <input className={`${classeCampo} w-48`} placeholder="Cerca un dato" aria-label="Cerca un dato"
                          value={cercaCampo} onChange={e => setCercaCampo(e.target.value)} />
                      </div>
                      <div className="flex max-h-[45vh] flex-wrap gap-1 overflow-auto rounded border border-line p-2">
                        {Object.entries(prova.campi).filter(([percorso, valore]) => !cercaCampo.trim()
                          || `${percorso} ${valore}`.toLowerCase().includes(cercaCampo.trim().toLowerCase())).map(([percorso, valore]) => (
                          <button key={percorso} type="button" onClick={() => aggiungiCampo(percorso)}
                            title={`{{${percorso}}}`}
                            className="rounded-full border border-line px-2 py-1 text-xs text-ink hover:bg-canvas">
                            {String(valore).slice(0, 40) || '(vuoto)'}
                            <span className="ml-1 text-muted">{percorso.split('.').slice(-2).join(' · ')}</span>
                          </button>
                        ))}
                      </div>
                    </>}
                  </div>
                )}
              </div>

              <Field label="2. Frase da mandare in chat" error={errori.reply_template}
                hint="I dati fra doppie graffe si riempiono a ogni chiamata. Rifai la prova per vedere il risultato.">
                {props => <textarea {...props} rows={2}
                  value={bozza.reply_template || ''}
                  onChange={e => setBozza({ ...bozza, reply_template: e.target.value })} />}
              </Field>

              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={!!bozza.use_ai}
                  onChange={e => setBozza({ ...bozza, use_ai: e.target.checked })} />
                <span>
                  Fai scrivere la risposta all’AI
                  <span className="block text-xs text-muted">
                    Più naturale, senza scrivere la frase; costa una chiamata e può aggiungere cose che nei
                    dati non c’erano. Se l’AI non risponde resta la frase qui sopra.
                  </span>
                </span>
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!bozza.enabled}
                  onChange={e => setBozza({ ...bozza, enabled: e.target.checked })} />
                Attivo: le automazioni possono usarlo
              </label>

              <details open={avanzate} onToggle={e => setAvanzate(e.currentTarget.open)}
                className="rounded-lg border border-line p-3">
                <summary className="cursor-pointer text-sm font-medium text-ink">Impostazioni avanzate</summary>
                <div className="mt-3 space-y-3">
                  <Field label="Indirizzo" error={errori.url_template}
                    hint="{{testo}} è quello che scrivi dopo la parola di comando, {{chiave}} la chiave. Solo https.">
                    {props => <input {...props}
                      placeholder="https://api.esempio.com/v1/meteo?key={{chiave}}&q={{testo}}"
                      value={bozza.url_template}
                      onChange={e => setBozza({ ...bozza, url_template: e.target.value })} />}
                  </Field>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Metodo">
                      {props => <select {...props}
                        value={bozza.method} onChange={e => setBozza({ ...bozza, method: e.target.value })}>
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                      </select>}
                    </Field>
                    <Field label="Attesa massima (secondi)" error={errori.timeout_seconds}>
                      {props => <input {...props} type="number" min={1} max={30}
                        value={bozza.timeout_seconds}
                        onChange={e => setBozza({ ...bozza, timeout_seconds: Number(e.target.value) })} />}
                    </Field>
                  </div>
                  {apis.length > 0 && <Field label="Fa parte dell’API">
                    {props => <select {...props} value={bozza.api || ''}
                      onChange={e => setBozza({ ...bozza, api: e.target.value ? Number(e.target.value) : null })}>
                      <option value="">Nessuna</option>
                      {apis.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>}
                  </Field>}
                  <Field label="Intestazioni" error={errori.headers}
                    hint="Una per riga, nella forma Nome: valore. Anche qui puoi usare {{chiave}}.">
                    {props => <textarea {...props} rows={2}
                      placeholder="Authorization: Bearer {{chiave}}"
                      value={intestazioni} onChange={e => setIntestazioni(e.target.value)} />}
                  </Field>
                  {bozza.method === 'POST' && (
                    <Field label="Corpo della richiesta" error={errori.body_template}>
                      {props => <textarea {...props} rows={3}
                        value={bozza.body_template || ''}
                        onChange={e => setBozza({ ...bozza, body_template: e.target.value })} />}
                    </Field>
                  )}
                  {bozza.has_secret && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!bozza.clear_secret}
                        onChange={e => setBozza({ ...bozza, clear_secret: e.target.checked })} />
                      Togli la chiave salvata
                    </label>
                  )}
                  <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1" checked={!!bozza.allow_local}
                      onChange={e => setBozza({ ...bozza, allow_local: e.target.checked })} />
                    <span>
                      È un servizio sulla mia rete
                      <span className="block text-xs text-muted">
                        Per un progetto sul tuo server, senza https e con indirizzo privato. Toglie la protezione
                        che impedisce alle automazioni di raggiungere la rete interna: solo per i tuoi servizi, e
                        solo l’amministratore può attivarlo.
                      </span>
                    </span>
                  </label>
                </div>
              </details>
            </div>
          </DialogBody>
          <DialogFooter>
            {bozza.id && (
              <Button onClick={() => navigate('/automations', {
                state: { servizio: bozza.id, parola: guida?.parola || '' },
              })}>Crea un’automazione con questo servizio</Button>
            )}
            <Button onClick={() => setBozza(null)}>Chiudi</Button>
            <Button variant="primary" loading={salvataggio} onClick={salva}>Salva</Button>
          </DialogFooter>
        </Modal>
      )}
    </section>
  );
}
